import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
import { onForward } from './constants';
import * as Yup from 'yup';
import Loader from '@Components/Loader';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import SBCountryDropdown from '@Components/Dropdown/Country';
import SbTextField from '@Components/Textfield';
import {
  // isDGaccessibleOptions,
  // isDGOptions,
  dgQuantityUnitOptions,
  isConsigneeAddressResidentialOptions,
  isShipperAddressResidentialOptions,
} from '../../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Emirates/constants';
import DatePicker from '@Components/DatePicker';
import Image from '@Components/Image';
import { setDocumentUploadEmiratesModalOpen } from '@Reducers/emiratesModalSlice/emiratesModalSlice';
import UploadEmiratesDoc from '../../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Emirates/UploadEmiratesDoc';
import dayjs from 'dayjs';

// ------- VALIDATION SCHEMA -------
const emiratesInformationSchema = (isSaudiArabia: boolean) =>
  Yup.object().shape({
    accountNo: Yup.mixed()
      .test('is-valid', 'Account is required', (value: any) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return true;
        return !!value;
      })
      .required('Account is required'),

    countryOfManufacture: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Country of Manufacture is required'),

    paymentMode: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Payment Mode is required'),

    serviceType: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Service Type is required'),

    shipmentPurpose: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Shipment Purpose is required'),

    shipmentType: Yup.string()
      .transform(value => (Array.isArray(value) ? '' : value))
      .required('Shipment Type is required'),

    isDG: Yup.boolean()
      .transform(value => (value === 'true' ? true : value === 'false' ? false : value))
      .required('DG selection is required'),

    isDGaccessible: Yup.boolean()
      .transform(value => (value === 'true' ? true : value === 'false' ? false : value))
      .required('DG Accessible selection is required'),

    shipperId: Yup.string().optional(),

    isConsigneeAddressResidential: Yup.boolean().nullable().required('Please select consignee address type'),

    // ✅ Conditional DG fields

    dgUnId: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('DG UN ID is required') : schema.notRequired(),
    ),

    dgPiId: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('DG PI ID is required') : schema.notRequired(),
    ),

    dgProperShippingName: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('Proper Shipping Name is required') : schema.notRequired(),
    ),

    dgClassDivision: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('Class/Division is required') : schema.notRequired(),
    ),

    dgPackingGroup: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('Packing Group is required') : schema.notRequired(),
    ),

    dgQuantity: Yup.string().when('isDG', ([isDG], schema) =>
      isDG
        ? schema.required('Quantity is required').test('is-valid-number', 'Must be a valid positive number', value => {
            if (!value) return false;
            const num = parseFloat(value);
            return !isNaN(num) && num >= 0;
          })
        : schema.notRequired(),
    ),

    dgQuantityUnit: Yup.string().when('isDG', ([isDG], schema) =>
      isDG ? schema.required('Quantity Unit is required') : schema.notRequired(),
    ),
    locationIdentifier: isSaudiArabia
      ? Yup.string().required('Location Identifier is required')
      : Yup.string().notRequired(),

    // nationalId: isSaudiArabia ? Yup.string().required('National ID is required') : Yup.string().notRequired(),

    // dateOfBirth: isSaudiArabia
    //   ? Yup.date().nullable().required('Date of Birth is required')
    //   : Yup.date().nullable().notRequired(),

    nationality: isSaudiArabia ? Yup.string().required('Nationality is required') : Yup.string().notRequired(),

    // expiryDate: isSaudiArabia
    //   ? Yup.date().nullable().required('Expiry Date is required')
    //   : Yup.date().nullable().notRequired(),

    // nameAsPerID: isSaudiArabia ? Yup.string().required('Name as per ID is required') : Yup.string().notRequired(),
  });

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showEmiratesDetails: boolean;
  btnText: string;
}

const UserEmirates = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showEmiratesDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const { shipmentById, userEmiratesDetails: userEmiratesSettings } = useSelector(
    (state: RootStateType) => state.shipment,
  );
  const { _consigneeId } = shipmentById || {};
  const isSaudiArabia = _consigneeId?.country === 'Saudi Arabia';

  const { emiratesDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const documentUploadEmiratesModalOpen = useSelector(
    (state: RootStateType) => state.emiratesModal.documentUploadEmiratesModalOpen,
  );

  // ------------ FORMIK ------------
  const { values, setFieldValue, errors, handleSubmit, handleChange } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      paymentMode: '',
      serviceType: '',
      shipmentPurpose: '',
      shipmentType: '',
      isDG: false,
      isDGaccessible: false,
      isConsigneeAddressResidential: null,
      isShipperAddressResidential: false,
      shipperId: '',
      dgUnId: '',
      dgPiId: '',
      dgProperShippingName: '',
      dgClassDivision: '',
      dgPackingGroup: '',
      dgQuantity: initialStates?.dgQuantity || 0,
      dgQuantityUnit: '',
      locationIdentifier: '',
      nationalId: '',
      dateOfBirth: null,
      nationality: isSaudiArabia ? 'SA' : '',
      expiryDate: null,
      nameAsPerID: '',
      ...initialStates,
      invoiceNumber: invoiceNo,
      accountNo: userEmiratesSettings?.accountNo?.length
        ? userEmiratesSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
      countryOfManufacture: userEmiratesSettings?.countryOfManufacture || '',
    },
    validationSchema: emiratesInformationSchema(isSaudiArabia),
    onSubmit(values) {
      // Prepare data for backend
      const submitValues = {
        ...values,
        // Convert string values to boolean for isDG and isDGaccessible
        isDG: typeof values.isDG === 'string' ? values.isDG === 'true' : values.isDG,
        isDGaccessible:
          typeof values.isDGaccessible === 'string' ? values.isDGaccessible === 'true' : values.isDGaccessible,
        isConsigneeAddressResidential:
          typeof values.isConsigneeAddressResidential === 'string'
            ? values.isConsigneeAddressResidential === 'true'
            : values.isConsigneeAddressResidential,
        isShipperAddressResidential:
          typeof values.isShipperAddressResidential === 'string'
            ? values.isShipperAddressResidential === 'true'
            : values.isShipperAddressResidential,
      };

      onForward({
        values: submitValues,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
        setLoading,
      });
    },
  });

  useEffect(() => {
    if (!isSaudiArabia) {
      setFieldValue('locationIdentifier', '');
      setFieldValue('nationalId', '');
      setFieldValue('dateOfBirth', null);
      setFieldValue('nationality', '');
      setFieldValue('expiryDate', null);
      setFieldValue('nameAsPerID', '');
    } else {
      // setFieldValue('locationIdentifier', '');
      setFieldValue('nationality', 'SA');
    }
  }, [isSaudiArabia]);

  return (
    <>
      {documentUploadEmiratesModalOpen && (
        <UploadEmiratesDoc
          shipmentNumber={shipmentById?.shipmentNumber || ''}
          csp={values?.csp}
          type={values?.type}
          open={documentUploadEmiratesModalOpen}
          handleClose={() => {
            dispatch(setDocumentUploadEmiratesModalOpen(false));
            // setShowAWB('');
          }}
          emiratesDetails={emiratesDetails}
        />
      )}
      <Loader overly loading={loading}></Loader>
      {showEmiratesDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container alignItems="center" justifyContent="space-between" mb={3}>
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  Emirates Information
                </Typography>
              </Grid>

              {emiratesDetails?.trackingNumber && (
                <Grid item>
                  <SbButton
                    variant="contained"
                    sx={{ borderRadius: `4px !important` }}
                    startIcon={<Image src={'/images/upload-cloud.svg'} alt={'documents'} width={26} />}
                    onClick={() => {
                      dispatch(setDocumentUploadEmiratesModalOpen(true));
                    }}
                  >
                    Forward Shipment Documents
                  </SbButton>
                </Grid>
              )}
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              {/* Account Number */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="accountNo"
                  label="Emirates Account"
                  required
                  placeholder="Select Account"
                  name="accountNo"
                  value={
                    Array.isArray(values?.accountNo)
                      ? values.accountNo.map((item: { value: any }) => item.value)
                      : values.accountNo
                      ? [values.accountNo]
                      : []
                  }
                  options={userEmiratesSettings?.accountNo ?? []}
                  onChange={(_, selectedObj) => {
                    setFieldValue('accountNo', selectedObj);
                  }}
                  error={errors.accountNo as string}
                />
              </Grid>

              {/* Country of Manufacture */}
              <Grid item xs={12} md={6}>
                <SBCountryDropdown
                  disabled={true}
                  required
                  label="Country of Manufacture"
                  value={values?.countryOfManufacture}
                  options={undefined}
                  onChange={value => {
                    setFieldValue('countryOfManufacture', value[0]);
                  }}
                  error={errors?.countryOfManufacture as string}
                />
              </Grid>

              {/* Payment Mode */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="paymentMode"
                  label="Payment Mode"
                  required
                  placeholder="Select Payment Mode"
                  name="paymentMode"
                  value={values.paymentMode ? [values.paymentMode] : []}
                  options={userEmiratesSettings?.paymentMode?.length ? userEmiratesSettings.paymentMode : []}
                  onChange={value => {
                    setFieldValue('paymentMode', value[0]);
                  }}
                  error={errors.paymentMode as string}
                />
              </Grid>

              {/* Service Type */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="serviceType"
                  label="Service Type"
                  required
                  placeholder="Select Service Type"
                  name="serviceType"
                  value={values.serviceType ? [values.serviceType] : []}
                  options={userEmiratesSettings?.serviceType?.length ? userEmiratesSettings.serviceType : []}
                  onChange={value => {
                    setFieldValue('serviceType', value[0]);
                  }}
                  error={errors.serviceType as string}
                />
              </Grid>

              {/* Shipment Purpose */}
              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="shipmentPurpose"
                  label="Shipment Purpose"
                  required
                  placeholder="Select Shipment Purpose"
                  name="shipmentPurpose"
                  value={values.shipmentPurpose ? [values.shipmentPurpose] : []}
                  options={userEmiratesSettings?.shipmentPurpose?.length ? userEmiratesSettings.shipmentPurpose : []}
                  onChange={value => {
                    setFieldValue('shipmentPurpose', value[0]);
                  }}
                  error={errors.shipmentPurpose as string}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="shipmentType"
                  label="Shipment Type"
                  required
                  placeholder="Select Shipment Type"
                  name="shipmentType"
                  value={values.shipmentType ? [values.shipmentType] : []}
                  options={userEmiratesSettings?.shipmentType?.length ? userEmiratesSettings.shipmentType : []}
                  onChange={value => {
                    setFieldValue('shipmentType', value[0]);
                  }}
                  error={errors.shipmentType as string}
                />
              </Grid>

              {/* isDG - Dangerous Goods */}
              {/* <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="isDG"
                  label="Dangerous Goods (DG)"
                  required
                  placeholder="Select"
                  name="isDG"
                  value={values.isDG !== undefined ? [values.isDG.toString()] : ['false']}
                  options={isDGOptions}
                  onChange={value => {
                    const boolValue = value[0] === 'true';
                    setFieldValue('isDG', boolValue);
                    // Reset DG fields when isDG is set to false
                    if (!boolValue) {
                      setFieldValue('dgUnId', '');
                      setFieldValue('dgPiId', '');
                      setFieldValue('dgProperShippingName', '');
                      setFieldValue('dgClassDivision', '');
                      setFieldValue('dgPackingGroup', '');
                      setFieldValue('dgQuantity', 0);
                      setFieldValue('dgQuantityUnit', '');
                    }
                  }}
                  error={errors.isDG as string}
                />
              </Grid> */}

              {/* isDGaccessible - DG Accessible */}
              {/* <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="isDGaccessible"
                  label="DG Accessible"
                  required
                  placeholder="Select"
                  name="isDGaccessible"
                  value={values.isDGaccessible !== undefined ? [values.isDGaccessible.toString()] : ['false']}
                  options={isDGaccessibleOptions}
                  onChange={value => {
                    setFieldValue('isDGaccessible', value[0]);
                  }}
                  error={errors.isDGaccessible as string}
                />
              </Grid> */}

              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="isShipperAddressResidential"
                  label="Is Shipper Address Residential?"
                  required
                  placeholder="Select"
                  name="isShipperAddressResidential"
                  value={
                    values.isShipperAddressResidential !== undefined
                      ? [values.isShipperAddressResidential.toString()]
                      : ['false']
                  }
                  options={isShipperAddressResidentialOptions}
                  onChange={value => {
                    setFieldValue('isShipperAddressResidential', value[0]);
                  }}
                  error={errors.isShipperAddressResidential as string}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SBDropdown
                  disabled={!isEditable}
                  id="isConsigneeAddressResidential"
                  label="Is Consignee Address Residential?"
                  required
                  placeholder="Select"
                  name="isConsigneeAddressResidential"
                  value={
                    values.isConsigneeAddressResidential !== null ? [String(values.isConsigneeAddressResidential)] : []
                  }
                  options={isConsigneeAddressResidentialOptions}
                  onChange={value => {
                    setFieldValue('isConsigneeAddressResidential', value[0] === 'true');
                  }}
                  error={errors.isConsigneeAddressResidential as string}
                />
              </Grid>

              {/* Shipper ID (Optional) */}
              <Grid item xs={12} md={6}>
                <SbTextField
                  disabled={!isEditable}
                  label="Shipper ID"
                  value={values.shipperId}
                  placeholder="Enter Shipper ID"
                  onChange={handleChange}
                  name="shipperId"
                  error={errors.shipperId as string}
                />
              </Grid>

              {/* DG Fields - Visible only when isDG is true */}
              {values.isDG && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 2 }}>
                      Dangerous Goods Information
                    </Typography>
                  </Grid>

                  {/* DG UN ID */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      required
                      label="DG UN ID"
                      value={values.dgUnId}
                      placeholder="Enter UN ID"
                      onChange={handleChange}
                      name="dgUnId"
                      error={errors.dgUnId as string}
                    />
                  </Grid>

                  {/* DG PI ID */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      required
                      label="DG PI ID"
                      value={values.dgPiId}
                      placeholder="Enter PI ID"
                      onChange={handleChange}
                      name="dgPiId"
                      error={errors.dgPiId as string}
                    />
                  </Grid>

                  {/* DG Proper Shipping Name */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      required
                      label="Proper Shipping Name"
                      value={values.dgProperShippingName}
                      placeholder="Enter Proper Shipping Name"
                      onChange={handleChange}
                      name="dgProperShippingName"
                      error={errors.dgProperShippingName as string}
                    />
                  </Grid>

                  {/* DG Class/Division */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      required
                      label="Class/Division"
                      value={values.dgClassDivision}
                      placeholder="Enter Class/Division"
                      onChange={handleChange}
                      name="dgClassDivision"
                      error={errors.dgClassDivision as string}
                    />
                  </Grid>

                  {/* DG Packing Group */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      required
                      label="Packing Group"
                      value={values.dgPackingGroup}
                      placeholder="Enter Packing Group"
                      onChange={handleChange}
                      name="dgPackingGroup"
                      error={errors.dgPackingGroup as string}
                    />
                  </Grid>

                  {/* DG Quantity */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      disabled={!isEditable}
                      required
                      label="Quantity"
                      type="string"
                      value={values.dgQuantity}
                      placeholder="Enter Quantity (e.g., 0.5, 1, 2.5)"
                      onChange={e => {
                        // Allow only numbers and decimal point
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      name="dgQuantity"
                      error={errors.dgQuantity as string}
                      showInputProps={false}
                    />
                  </Grid>

                  {/* DG Quantity Unit */}
                  <Grid item xs={12} md={6}>
                    <SBDropdown
                      disabled={!isEditable}
                      id="dgQuantityUnit"
                      label="Quantity Unit"
                      required
                      placeholder="Select Unit"
                      name="dgQuantityUnit"
                      value={values.dgQuantityUnit ? [values.dgQuantityUnit] : []}
                      options={dgQuantityUnitOptions}
                      onChange={value => {
                        setFieldValue('dgQuantityUnit', value[0]);
                      }}
                      error={errors.dgQuantityUnit as string}
                    />
                  </Grid>
                </>
              )}

              {isSaudiArabia && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                      Saudi Arabia KYC Information
                    </Typography>
                  </Grid>

                  {/* Location Identifier */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      required
                      label="Location Identifier"
                      name="locationIdentifier"
                      value={values.locationIdentifier}
                      onChange={handleChange}
                      error={errors.locationIdentifier as string}
                      disabled={!isEditable}
                    />
                  </Grid>

                  {/* National ID */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      // required
                      label="National ID"
                      name="nationalId"
                      value={values.nationalId}
                      onChange={handleChange}
                      error={errors.nationalId as string}
                      disabled={!isEditable}
                    />
                  </Grid>

                  {/* Date Of Birth */}
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      // required
                      label="Date of Birth"
                      value={values.dateOfBirth || null}
                      inputFormat={'YYYY-MM-DD'}
                      handleChange={value => {
                        setFieldValue('dateOfBirth', value);
                      }}
                      disabled={!isEditable}
                      maxDate={dayjs()}
                      error={errors.dateOfBirth as string}
                    />
                  </Grid>

                  {/* Nationality */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      // required
                      label="Nationality"
                      name="nationality"
                      value={values.nationality}
                      onChange={handleChange}
                      error={errors.nationality as string}
                      disabled
                    />
                  </Grid>

                  {/* Expiry Date */}
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      // required
                      label="Expiry Date"
                      inputFormat={'YYYY-MM-DD'}
                      value={values.expiryDate || null}
                      handleChange={value => {
                        setFieldValue('expiryDate', value);
                      }}
                      disabled={!isEditable}
                      error={errors.expiryDate as string}
                    />
                  </Grid>

                  {/* Name As Per ID */}
                  <Grid item xs={12} md={6}>
                    <SbTextField
                      // required
                      label="Name As Per ID"
                      name="nameAsPerID"
                      value={values.nameAsPerID}
                      onChange={handleChange}
                      error={errors.nameAsPerID as string}
                      disabled={!isEditable}
                    />
                  </Grid>
                </>
              )}

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {emiratesDetails?.trackingNumber && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserEmirates;
